import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Form, Modal, Table } from 'src/common';
import { useTranslation, Trans } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import styles from '../index.module.scss';
import {
  DEFAULT_KEYWORD_SETTING,
  FilterLogic,
  SearchType,
  HighlightColor,
  KEYWORD_LIMIT,
  SELECTABLE_LOGIC
} from 'src/models/matchingProfiles/types';
import DeleteButtonQuickFilter from '../DeleteButtonQuickFilter';
import SelectFieldQuickFilter from '../SelectFieldQuickFilter';
import InfoLabel from 'src/shared/InfoLabel';
import { useFeatureFlag, FeatureFlag, useMyFeaturesAndOwnership } from 'src/helpers/featureFlag';
import { usePrefixSearchLimit } from 'src/models/matchingProfiles/hooks';
import { checkIfOptionIsEnabled } from 'src/shared/MatchingProfile/Keywords/helpers';
import { Keyword, SearchLogic, fillKeywordDataWithDefaultValues } from '@tendium/prom-types';
import KeywordsLimitWarnModal from 'src/shared/MatchingProfile/Keywords/KeywordsArea/KeywordsLimitWarnModal';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import KeywordsInput from 'src/shared/Keywords/KeywordsInput';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Props {
  onClose: () => void;
  disabled?: boolean;
  keywords: Keyword[];
}

function useTableColumns(isPrefixSearchLimit: boolean, disabled?: boolean): ColumnProps<Keyword>[] {
  const { t } = useTranslation();
  const MPBroadOrNarrowSearchFlag = useFeatureFlag(FeatureFlag.MatchingProfile_BroadOrNarrowSearchInDocuments);
  const MPHighlightSearchFlag = useFeatureFlag(FeatureFlag.MatchingProfile_HighlightSearchInDocuments);
  const prefixSearchLimit = usePrefixSearchLimit();

  return useMemo((): ColumnProps<Keyword>[] => {
    return [
      {
        title: t('Tenders.keyword'),
        key: 'value',
        dataIndex: 'value',
        className: styles.colKeyword,
        render: value => <span className={styles.keyword}>{value}</span>
      },
      {
        title: <InfoLabel title={t('Tenders.whereToSearch')} desc={<Trans i18nKey={'Tenders.whereToSearchDesc'} />} />,
        key: 'searchType',
        dataIndex: 'searchType',
        className: styles.colSearchTypeSetting,
        render: (searchType: SearchType, record) => (
          <SelectFieldQuickFilter
            values={Object.values(SearchType).map(type => ({
              value: type,
              label: t(`Tenders.SearchType.${type}`),
              disabled: !checkIfOptionIsEnabled(
                record.filterLogic ?? FilterLogic.BroadSearch,
                type,
                MPBroadOrNarrowSearchFlag,
                MPHighlightSearchFlag
              )
            }))}
            className={styles.searchTypeSelect}
            selectedValue={searchType || DEFAULT_KEYWORD_SETTING.searchType}
            label={t('Tenders.whereToSearch')}
            field={'searchType'}
            keyword={record}
            disabled={disabled}
          />
        )
      },
      {
        title: <InfoLabel title={t('Tenders.filterLogic')} desc={<Trans i18nKey={'Tenders.filterLogicDesc'} />} />,
        key: 'filterLogic',
        dataIndex: 'filterLogic',
        className: styles.colSetting,
        render: (filterLogic: FilterLogic, record) => (
          <SelectFieldQuickFilter
            values={Object.values(FilterLogic).map(logicType => ({
              value: logicType,
              label: t(`Tenders.FilterLogic.${logicType}`),
              disabled: !checkIfOptionIsEnabled(
                logicType,
                record.searchType ?? SearchType.Notice,
                MPBroadOrNarrowSearchFlag,
                MPHighlightSearchFlag
              )
            }))}
            selectedValue={filterLogic || DEFAULT_KEYWORD_SETTING.filterLogic}
            label={t('Tenders.filterLogic')}
            field={'filterLogic'}
            keyword={record}
            disabled={disabled}
          />
        )
      },
      {
        title: <InfoLabel title={t('Tenders.searchLogic')} desc={<Trans i18nKey={'Tenders.searchLogicDesc'} />} />,
        key: 'searchLogic',
        dataIndex: 'searchLogic',
        className: styles.colSetting,
        render: (searchLogic: SearchLogic, record) => (
          <SelectFieldQuickFilter
            values={SELECTABLE_LOGIC.map(el => ({
              value: el,
              label: t(`Tenders.SearchLogic.${el}`),
              disabled: isPrefixSearchLimit && el === SearchLogic.PrefixMatch,
              tooltip:
                isPrefixSearchLimit && el === SearchLogic.PrefixMatch
                  ? t('Tenders.prefixSearchLimitReached', { keywords: prefixSearchLimit })
                  : undefined
            }))}
            selectedValue={searchLogic || DEFAULT_KEYWORD_SETTING.searchLogic}
            label={t('Tenders.searchLogic')}
            field={'searchLogic'}
            keyword={record}
            disabled={disabled}
          />
        )
      },
      {
        title: (
          <InfoLabel title={t('Tenders.highlightColour')} desc={<Trans i18nKey={'Tenders.highlightsColourDesc'} />} />
        ),
        key: 'highlightColor',
        dataIndex: 'highlightColor',
        className: styles.colAction,
        render: (highlightColor: HighlightColor, record) => (
          <>
            <SelectFieldQuickFilter
              values={Object.keys(HighlightColor).map(el => ({ value: el, label: t(`Tenders.HighlightColor.${el}`) }))}
              selectedValue={highlightColor || DEFAULT_KEYWORD_SETTING.highlightColor}
              label={t('Tenders.highlightColour')}
              field={'highlightColor'}
              keyword={record}
              disabled={disabled}
            />
            {!disabled && <DeleteButtonQuickFilter keyword={record} />}
          </>
        )
      }
    ];
  }, [MPBroadOrNarrowSearchFlag, MPHighlightSearchFlag, disabled, isPrefixSearchLimit, prefixSearchLimit, t]);
}

export const KeywordsModalQuickFilter: FC<Props> = ({ onClose, disabled, keywords }) => {
  const prefixSearchLimit = usePrefixSearchLimit();
  const { t } = useTranslation();
  const [, updateSearchArgs] = useApiTenders();
  const [form] = Form.useForm();
  const [restKeywords, setRestKeywords] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState(false);

  const fillKeywordsInfo = useMyFeaturesAndOwnership();

  const onShowWarning = useCallback(() => setShowWarning(true), []);
  const onHideWarning = useCallback(() => {
    setShowWarning(false);
    setRestKeywords([]);
  }, []);

  const isPrefixSearchLimit = useMemo(
    () => keywords.filter(keyword => keyword.searchLogic === SearchLogic.PrefixMatch).length >= prefixSearchLimit,
    [keywords, prefixSearchLimit]
  );
  const columns = useTableColumns(isPrefixSearchLimit, disabled);

  const addKeyword = useCallback(
    (userInput?: string): void => {
      if (!userInput) return;
      const currentAmountOfKeywords = (keywords || []).length;
      let uniqFromInput = [
        ...new Set(
          userInput
            .split(',')
            .map(_ => _.trim())
            .filter(_ => !!_)
        )
      ];

      const uniqueFromKeywords = uniqFromInput.filter(input => !keywords?.map(_ => _.value).includes(input));

      if (currentAmountOfKeywords + uniqFromInput.length > KEYWORD_LIMIT) {
        const sliceIndex = KEYWORD_LIMIT - currentAmountOfKeywords;
        setRestKeywords(uniqFromInput.slice(sliceIndex));
        uniqFromInput = uniqFromInput.slice(0, sliceIndex);
        onShowWarning();
      }

      if (!!uniqueFromKeywords.length && !disabled) {
        const newKeywords: Keyword[] = uniqueFromKeywords.map(keyword => {
          return fillKeywordDataWithDefaultValues(
            { value: keyword },
            fillKeywordsInfo.myFeatureFlags,
            fillKeywordsInfo.myOwnership
          );
        });
        const newKeywordsState: Keyword[] = [...keywords, ...newKeywords];
        updateSearchArgs({ keywordArgs: newKeywordsState });
      }

      form.setFieldsValue({
        keyword: ''
      });
    },
    [
      disabled,
      form,
      keywords,
      onShowWarning,
      fillKeywordsInfo.myFeatureFlags,
      fillKeywordsInfo.myOwnership,
      updateSearchArgs
    ]
  );

  return (
    <Modal
      title={t('Tenders.advancedKeywordSettings')}
      visible
      onCancel={onClose}
      width={890}
      className={styles.modal}
      footer={
        <Button type={'primary'} loading={false} onClick={onClose}>
          {t('Common.done')}
        </Button>
      }
    >
      <Table className={styles.table} columns={columns} dataSource={keywords} rowKey={'id'} />
      <div className={styles.footer}>
        <div className={styles.keywords}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <div className={styles.keywordsList}>
            <KeywordsInput addKeyword={addKeyword} disabled={disabled} label={t('ExpiringContracts.addKeyword')} />
          </div>
        </div>
        {!disabled && (
          <Button type={'primary'} onClick={() => addKeyword} disabled={disabled}>
            {t('Common.addKeywords')}
          </Button>
        )}
        {showWarning && !!restKeywords.length && (
          <KeywordsLimitWarnModal onClose={onHideWarning} keywords={restKeywords} />
        )}
      </div>
    </Modal>
  );
};

export default KeywordsModalQuickFilter;
